import { customAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const cloudOptions = [
  { value: "wildfire.paloaltonetworks.com", label: "Global (US)" },
  { value: "eu.wildfire.paloaltonetworks.com", label: "EU" },
  { value: "jp.wildfire.paloaltonetworks.com", label: "Japan" },
  { value: "sg.wildfire.paloaltonetworks.com", label: "Singapore" },
  { value: "uk.wildfire.paloaltonetworks.com", label: "UK" },
  { value: "ca.wildfire.paloaltonetworks.com", label: "Canada" },
  { value: "au.wildfire.paloaltonetworks.com", label: "Australia" },
  { value: "de.wildfire.paloaltonetworks.com", label: "Germany" },
  { value: "in.wildfire.paloaltonetworks.com", label: "India" },
  { value: "ch.wildfire.paloaltonetworks.com", label: "Switzerland" },
  { value: "pl.wildfire.paloaltonetworks.com", label: "Poland" },
  { value: "id.wildfire.paloaltonetworks.com", label: "Indonesia" },
  { value: "tw.wildfire.paloaltonetworks.com", label: "Taiwan" },
  { value: "fr.wildfire.paloaltonetworks.com", label: "France" },
  { value: "qatar.wildfire.paloaltonetworks.com", label: "Qatar" },
  { value: "kr.wildfire.paloaltonetworks.com", label: "South Korea" },
  { value: "za.wildfire.paloaltonetworks.com", label: "South Africa" },
  { value: "il.wildfire.paloaltonetworks.com", label: "Israel" },
  { value: "sa.wildfire.paloaltonetworks.com", label: "Saudi Arabia" },
  { value: "it.wildfire.paloaltonetworks.com", label: "Italy" },
  { value: "es.wildfire.paloaltonetworks.com", label: "Spain" },
  { value: "br.wildfire.paloaltonetworks.com", label: "Brazil" },
];

const cloudField = {
  kind: "select",
  label: "Cloud",
  required: true,
  target: { kind: "configuration", field: "cloud" },
  options: cloudOptions,
  helperText:
    "The WildFire cloud region for your account. Credentials apply only to its /publicapi/get/ and /publicapi/submit/ routes, not public test downloads or other Palo Alto Networks APIs.",
} as const;

function wildfireHost(cloud: string | undefined) {
  return (
    cloudOptions.find((option) => option.value === cloud)?.value ??
    "wildfire.paloaltonetworks.com"
  );
}

function wildfireUrlPattern({ cloud }: { cloud?: string }) {
  return `^https://${wildfireHost(cloud).replaceAll(".", "\\.")}/publicapi/(get|submit)/.*`;
}

function verdictRequest({ cloud, agent }: { cloud?: string; agent?: string }) {
  const body = new URLSearchParams({
    hash: "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
  });
  if (agent === "pcc" || agent === "prismaaccessapi") {
    body.set("agent", agent);
  }
  return {
    url: `https://${wildfireHost(cloud)}/publicapi/get/verdict`,
    method: "POST" as const,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
    expectedStatus: 200,
    expectedBodyContains: ["<get-verdict-info>", "<verdict>", "</verdict>"],
  };
}

export const panWildfire = defineProduct({
  name: "PAN WildFire",
  tagLine: "Cloud-based malware analysis",
  logo,
  tags: ["security"],
  variants: [
    productVariant({
      identifier: "panWildfire",
      authLabel: "API key (legacy)",
      protocol: "http",
      defaultUrlPattern: wildfireUrlPattern,
      auth: customAuth("panWildfire"),
      steps: [
        {
          title: "Get your API key",
          items: [
            {
              kind: "markdown",
              content:
                "In the WildFire portal, go to **Account > API Keys** to retrieve your API key.",
            },
          ],
        },
        {
          title: "Enter your WildFire details",
          items: [
            cloudField,
            {
              kind: "input",
              label: "API key",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
            {
              kind: "select",
              label: "API key type",
              target: { kind: "configuration", field: "agent" },
              defaultValue: "",
              options: [
                { value: "", label: "Standard WildFire API key" },
                { value: "prismaaccessapi", label: "Prisma Access" },
                { value: "pcc", label: "Prisma Cloud Compute" },
              ],
              helperText: "Select the product that issued your API key.",
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: verdictRequest,
      },
      usageHint: ({ cloud, agent }) =>
        `Use https://${wildfireHost(cloud)}/publicapi/ for WildFire requests. The connector supplies the apikey form field.${agent === "pcc" || agent === "prismaaccessapi" ? ` Include agent=${agent} in request form fields where required.` : ""} Credentials are not applied to public test downloads or other API families.`,
    }),
    productVariant({
      identifier: "panWildfireOauth",
      authLabel: "Service account (OAuth 2.0)",
      protocol: "http",
      defaultUrlPattern: wildfireUrlPattern,
      auth: oauthAuth,
      steps: [
        {
          title: "Prepare your WildFire service account",
          items: [
            {
              kind: "markdown",
              content:
                "In Strata Cloud Manager, [create or migrate an Advanced WildFire cloud API key](https://docs.paloaltonetworks.com/wildfire/u-v/wildfire-api/get-started-with-the-wildfire-api/wildfire-api-token-authentication/get-your-advanced-wildfire-cloud-api-token) under your licensed tenant and bind a unique service account. Assign **All Apps & Services** and a custom role containing only **iam.service_account** and **iam.custom_role**. Wait for the API key status to become **Valid**. Save the bound service account’s **Client ID**, **Client Secret**, and **TSG-ID**; the service account must belong to that TSG.",
            },
          ],
        },
        {
          title: "Enter your WildFire service account details",
          items: [
            cloudField,
            {
              kind: "input",
              label: "TSG-ID",
              required: true,
              target: { kind: "configuration", field: "tsgId" },
              helperText:
                "The Tenant Service Group ID for the bound service account.",
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
        {
          title: "Connect your service account",
          items: [
            {
              kind: "oauth",
              dependsOn: [
                { kind: "configuration", field: "cloud" },
                { kind: "configuration", field: "tsgId" },
                { kind: "oauthConnection", field: "clientId" },
                { kind: "oauthConnection", field: "clientSecret" },
              ],
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: () =>
          "https://auth.apps.paloaltonetworks.com/am/oauth2/access_token",
        clientCredentialsFrom: "db",
        tokenEndpointAuthMethod: () => "client_secret_basic_no_urlencode",
        scopes: ({ tsgId }) => [`tsg_id:${tsgId ?? ""}`],
      },
      connectionTest: {
        protocol: "http",
        request: verdictRequest,
      },
      usageHint: ({ cloud }) =>
        `Use https://${wildfireHost(cloud)}/publicapi/ for WildFire requests. The connector obtains and renews short-lived Bearer tokens automatically and supplies the Authorization header; omit the legacy apikey form field. Credentials are not applied to public test downloads or other API families.`,
    }),
  ],
});
